/**
 * Damage Calculation and Floating Numbers System
 */

export interface FloatingNumber {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  text: string;
  color: string;
  fontSize: number;
  alpha: number;
  lifeMs: number;
  maxLifeMs: number;
  isCrit: boolean;
}

export class DamageSystem {
  private floatingNumbers: FloatingNumber[] = [];

  /**
   * Calculate effective damage using standard formula: max(1, attack * mult - defense * 0.5)
   */
  public static calculateDamage(
    baseAttack: number,
    defense: number,
    multiplier: number = 1.0,
    critChance: number = 0.15,
    critMultiplier: number = 1.5
  ): { damage: number; isCrit: boolean } {
    const isCrit = Math.random() < critChance;
    const rawAttack = baseAttack * multiplier * (isCrit ? critMultiplier : 1.0);
    const mitigation = defense * 0.5;
    const variance = 0.92 + Math.random() * 0.16; // +/- 8% damage spread
    const finalDamage = Math.max(1, Math.round((rawAttack - mitigation) * variance));

    return { damage: finalDamage, isCrit };
  }

  /**
   * Spawn a floating damage number
   */
  public spawnFloatingNumber(
    x: number,
    y: number,
    text: string,
    color: string = "#ffffff",
    isCrit: boolean = false,
    isHeal: boolean = false
  ) {
    this.floatingNumbers.push({
      id: `num_${Date.now()}_${Math.random()}`,
      x: x + (Math.random() - 0.5) * 16,
      y: y - 12,
      vx: (Math.random() - 0.5) * 25,
      vy: isCrit ? -70 : isHeal ? -50 : -45,
      text,
      color: isHeal ? "#44ff88" : isCrit ? "#ffdd33" : color,
      fontSize: isCrit ? 16 : isHeal ? 13 : 11,
      alpha: 1.0,
      lifeMs: 0,
      maxLifeMs: isCrit ? 800 : 650,
      isCrit,
    });
  }

  /**
   * Update floating numbers
   */
  public update(dt: number) {
    const dtMs = dt * 1000;
    for (let i = this.floatingNumbers.length - 1; i >= 0; i--) {
      const num = this.floatingNumbers[i];
      num.lifeMs += dtMs;
      num.x += num.vx * dt;
      num.y += num.vy * dt;
      num.vy += 60 * dt; // slight downward gravity deceleration

      const progress = num.lifeMs / num.maxLifeMs;
      num.alpha = Math.max(0, 1 - progress * progress);

      if (num.lifeMs >= num.maxLifeMs) {
        this.floatingNumbers.splice(i, 1);
      }
    }
  }

  /**
   * Render floating numbers
   */
  public render(ctx: CanvasRenderingContext2D) {
    if (this.floatingNumbers.length === 0) return;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (const num of this.floatingNumbers) {
      const progress = num.lifeMs / num.maxLifeMs;
      // Spring pop scale: start at 1.4x and settle to 1.0x
      const scale = progress < 0.2 ? 1.0 + (0.2 - progress) * 2.2 : 1.0;

      ctx.save();
      ctx.globalAlpha = num.alpha;
      ctx.translate(num.x, num.y);
      ctx.scale(scale, scale);
      ctx.font = `bold ${num.fontSize}px 'Courier New', monospace, sans-serif`;

      // Shadow / outline
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = num.isCrit ? 3.5 : 2;
      ctx.strokeText(num.text, 0, 0);

      // Main text
      ctx.fillStyle = num.color;
      ctx.fillText(num.text, 0, 0);
      ctx.restore();
    }

    ctx.restore();
  }

  public clear() {
    this.floatingNumbers = [];
  }
}
